/** Shared types, constants, and helpers for AllLogs and MyLogs */

export interface LogEntry {
  _id: string;
  userId?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    employeeId?: string;
  };
  action: string;
  entity: string;
  entityId?: string;
  /** Set on patient-related logs (snapshot taken when the log was written). */
  patientId?: string;
  patientName?: string;
  regNo?: string;
  details?: string | object;
  createdAt: string;
}

export interface LogsResponse {
  data: LogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FilterForm {
  dateFrom?: string;
  dateTo?: string;
  action?: string;
  entity?: string;
  userId?: string;
  /** Patient name or Reg No, matched server-side. */
  search?: string;
  /** Set by clicking a patient in the table. */
  patientId?: string;
}

/**
 * Every action the backend writes, grouped, with its label. The dropdowns are
 * generated from this map so a filter can never offer an action nothing
 * writes (ADD_ROLE / UPDATE_TREATMENT used to) or miss one that exists.
 * Keep in sync with `grep -rhoE 'action: "[A-Z_]+"'` on the backend.
 */
export const ACTION_LABELS: Record<string, string> = {
  // Patients
  ADD_PATIENT: "Added patient",
  UPDATE_PATIENT: "Updated patient",
  DELETE_PATIENT: "Deleted patient",
  CHANGE_PATIENT_REG_NO: "Changed patient Reg No",
  CONFIRM_PROVISIONAL_PATIENT: "Confirmed online-booking patient",
  REASSIGN_PROVISIONAL_PATIENT: "Moved online booking to existing patient",
  IMPORT_PATIENTS_CSV: "Imported patients from CSV",
  DELETE_ALL_PATIENTS: "Deleted all patients",
  // Appointments
  CREATE_APPOINTMENT: "Created appointment",
  RESCHEDULE_APPOINTMENT: "Rescheduled appointment",
  UPDATE_APPOINTMENT_STATUS: "Updated appointment status",
  CANCEL_APPOINTMENT: "Canceled appointment",
  COMPLETE_APPOINTMENT: "Completed appointment",
  UPDATE_ONLINE_APPOINTMENT: "Updated online appointment",
  TRANSFER_APPOINTMENT_DOCTOR: "Transferred appointment to another doctor",
  SWAP_APPOINTMENT_PATIENT: "Changed appointment patient",
  UPDATE_APPOINTMENT_QUEUE: "Updated queue number",
  GENERATE_QUEUE: "Generated token",
  SEND_APPOINTMENT_CONFIRM_SMS: "Sent appointment confirmation SMS",
  // Treatment sheet
  CREATE_TREATMENT: "Created treatment sheet",
  DELETE_TREATMENT: "Deleted treatment sheet",
  ADD_TREATMENT_ITEM: "Added treatment item",
  REMOVE_TREATMENT_ITEM: "Removed treatment item",
  UPDATE_TREATMENT_ITEM: "Updated treatment item",
  UPDATE_PAID_TREATMENT_ITEM: "Updated paid treatment item",
  UPDATE_TREATMENT_ITEM_STATUS: "Updated treatment item status",
  ADD_CASE_TREATMENT: "Added case treatment",
  DELETE_CASE_TREATMENT: "Deleted case treatment",
  UPDATE_CASE_TREATMENT: "Updated case treatment",
  UPDATE_PAID_CASE_TREATMENT: "Updated paid case treatment",
  UPDATE_CASE_TREATMENT_STATUS: "Updated case treatment status",
  ADD_DIAGNOSE: "Added diagnosis",
  REMOVE_DIAGNOSE: "Removed diagnosis",
  UPDATE_SURFACE_DIAGNOSE: "Updated surface diagnosis",
  UPDATE_ROOT_DIAGNOSES: "Updated root (left/right/hide)",
  UPDATE_ROOT_VARIANT_DIAGNOSES: "Updated root treatment (e.g. Root Canal)",
  UPDATE_ADVANCED_DIAGNOSE_NOTE: "Updated tooth note",
  // Prescriptions
  ADD_PRESCRIPTION: "Added prescription",
  UPDATE_PRESCRIPTION: "Updated prescription",
  REMOVE_PRESCRIPTION: "Removed prescription",
  // Payments
  ADD_PAYMENT: "Added payment",
  EDIT_PAYMENT: "Edited payment",
  DELETE_PAYMENT: "Deleted payment",
  // Employees
  ADD_EMPLOYEE: "Added employee",
  UPDATE_EMPLOYEE: "Updated employee",
  UPDATE_EMPLOYEE_BASIC_INFO: "Updated employee details",
  UPDATE_EMPLOYEE_PROFESSIONAL_DETAILS: "Updated employee professional details",
  UPDATE_EMPLOYEE_PROFILE_PHOTO: "Updated employee photo",
  UPDATE_EMPLOYEE_CALENDAR_COLOR: "Updated employee calendar colour",
  UPDATE_EMPLOYEE_TYPE: "Updated employee type",
  UPDATE_EMPLOYEE_PASSWORD: "Changed employee password",
  ACTIVATE_EMPLOYEE: "Activated employee",
  DEACTIVATE_EMPLOYEE: "Deactivated employee",
  // Roles
  ADD_ROLE: "Added role",
  UPDATE_ROLE: "Updated role",
  ACTIVATE_ROLE: "Activated role",
  DEACTIVATE_ROLE: "Deactivated role",
  // Inventory
  CREATE_PRODUCT: "Created product",
  UPDATE_PRODUCT: "Updated product",
  UPDATE_PRODUCT_PRICE: "Updated product price",
  DELETE_PRODUCT: "Deleted product",
  CREATE_CATEGORY: "Created category",
  UPDATE_CATEGORY: "Updated category",
  DELETE_CATEGORY: "Deleted category",
  CREATE_SUB_CATEGORY: "Created sub category",
  UPDATE_SUB_CATEGORY: "Updated sub category",
  DELETE_SUB_CATEGORY: "Deleted sub category",
  CREATE_BRAND: "Created brand",
  UPDATE_BRAND: "Updated brand",
  DELETE_BRAND: "Deleted brand",
  CREATE_SUPPLIER: "Created supplier",
  UPDATE_SUPPLIER: "Updated supplier",
  DELETE_SUPPLIER: "Deleted supplier",
  CREATE_SCALE: "Created unit of measure",
  UPDATE_SCALE: "Updated unit of measure",
  DELETE_SCALE: "Deleted unit of measure",
  CREATE_PURCHASE: "Created purchase",
  UPDATE_PURCHASE: "Updated purchase",
  CREATE_PURCHASE_ORDER: "Created purchase order",
  UPDATE_PURCHASE_ORDER: "Updated purchase order",
  UPDATE_PURCHASE_ORDER_STATUS: "Updated purchase order status",
  RELEASE_TO_PATIENT: "Released products to patient",
  RELEASE_TO_EMPLOYEE: "Released products to staff",
  RELEASE_TO_CLINIC: "Released products for clinic use",
  RELEASE_FOR_TREATMENT: "Released products for a treatment stage",
  UPDATE_RELEASE_FOR_TREATMENT: "Changed products used on a treatment stage",
  RETURN_RELEASE_FOR_TREATMENT: "Returned a treatment stage's products to stock",
};

export const ENTITY_LABELS: Record<string, string> = {
  patient: "Patient",
  appointment: "Appointment",
  treatment: "Treatment",
  diagnose: "Diagnosis",
  prescription: "Prescription",
  payment: "Payment",
  employee: "Employee",
  role: "Role",
  product: "Product",
  category: "Category",
  subCategory: "Sub category",
  brand: "Brand",
  supplier: "Supplier",
  scale: "Unit of measure",
  purchase: "Purchase",
  purchaseOrder: "Purchase order",
  release: "Release",
};

/** Dropdown options are the label maps' keys, in the grouped order above. */
export const ACTION_OPTIONS: string[] = Object.keys(ACTION_LABELS);
export const ENTITY_OPTIONS: string[] = Object.keys(ENTITY_LABELS);

const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  appointment: "Pending",
  pending: "Pending",
  inqueue: "In queue",
  ongoing: "Ongoing",
  completed: "Completed",
  canceled: "Canceled",
  cancelled: "Canceled",
  noshow: "No show",
  tray: "Reschedule tray",
};

function getAppointmentStatusLabel(status: unknown): string | undefined {
  if (status == null) return undefined;
  const raw = String(status).trim();
  if (!raw) return undefined;
  const key = raw.toLowerCase();
  return APPOINTMENT_STATUS_LABELS[key] || raw;
}

export function getActionLabel(action: string): string {
  return ACTION_LABELS[action] || action.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getEntityLabel(entity: string): string {
  return ENTITY_LABELS[entity] || entity;
}

export interface FormatDetailsOptions {
  /** Skip patient name / reg no — used when the table already shows them in their own columns. */
  omitPatient?: boolean;
}

export function formatDetailsReadable(
  details: string | object | undefined,
  options: FormatDetailsOptions = {}
): string {
  if (details == null || details === "") return "—";
  if (typeof details === "string") return details.trim();
  if (typeof details !== "object") return String(details);
  const d = details as Record<string, unknown>;
  const parts: string[] = [];
  const showPatient = !options.omitPatient;
  if (showPatient && d.patientName != null && String(d.patientName).trim()) parts.push(`Patient: ${String(d.patientName).trim()}`);
  if (d.invoiceNo != null && String(d.invoiceNo).trim()) parts.push(`invoiceNo: ${String(d.invoiceNo).trim()}`);
  if (showPatient && d.regNo != null && String(d.regNo).trim()) parts.push(`Reg No: ${String(d.regNo).trim()}`);
  if (d.method != null && String(d.method).trim()) parts.push(`Method: ${String(d.method).trim()}`);
  if (d.employeeName != null && String(d.employeeName).trim()) parts.push(`Employee: ${String(d.employeeName).trim()}`);
  const statusLabel = getAppointmentStatusLabel(d.status);
  if (statusLabel) parts.push(`Status: ${statusLabel}`);
  if (d.purposeName != null && String(d.purposeName).trim()) parts.push(`Purpose: ${String(d.purposeName).trim()}`);
  if (Array.isArray(d.updatedFields) && d.updatedFields.length) {
    parts.push(`Updated: ${(d.updatedFields as string[]).join(", ")}`);
  }
  if (d.productName != null && String(d.productName).trim()) parts.push(`product: ${String(d.productName).trim()}`);
  if (d.categoryName != null && String(d.categoryName).trim()) parts.push(`category: ${String(d.categoryName).trim()}`);
  if (d.brandName != null && String(d.brandName).trim()) parts.push(`brand: ${String(d.brandName).trim()}`);
  if (d.supplierName != null && String(d.supplierName).trim()) parts.push(`supplier: ${String(d.supplierName).trim()}`);
  if (d.netTotal != null) parts.push(`netTotal: ${d.netTotal}`);
  if (d.total != null) parts.push(`total: ${d.total}`);
  if (d.scale != null && String(d.scale).trim()) parts.push(`scale: ${String(d.scale).trim()}`);
  if (d.unit != null && String(d.unit).trim()) parts.push(`unit: ${String(d.unit).trim()}`);
  if (d.newPrice != null) parts.push(`newPrice: ${d.newPrice}`);
  if (d.alertQuantity != null) parts.push(`alertQuantity: ${d.alertQuantity}`);
  if (d.addedPayments != null) parts.push(`addedPayments: ${d.addedPayments}`);
  if (d.addedProductLines != null) parts.push(`addedProductLines: ${d.addedProductLines}`);
  if (d.type != null && String(d.type).trim()) parts.push(`type: ${String(d.type).trim()}`);
  if (d.suppliercompany != null && String(d.suppliercompany).trim()) parts.push(`company: ${String(d.suppliercompany).trim()}`);
  const removedItem = d.removedItem as Record<string, unknown> | undefined;
  if (removedItem && typeof removedItem === "object" && !Array.isArray(removedItem)) {
    const itemParts: string[] = [];
    if (removedItem.treatment != null) itemParts.push(`treatment: ${removedItem.treatment}`);
    if (removedItem.price != null) itemParts.push(`price: ${removedItem.price}`);
    if (removedItem.toothNo != null) itemParts.push(`toothNo: ${removedItem.toothNo}`);
    if (removedItem.type != null) itemParts.push(`type: ${removedItem.type}`);
    if (removedItem.subtype != null) itemParts.push(`subtype: ${removedItem.subtype}`);
    if (itemParts.length) parts.push(`removedItem: ${itemParts.join(", ")}`);
  }
  // Hide raw IDs / nested payloads so logs show only human-readable populated values.
  const knownKeys = [
    "patientName",
    "invoiceNo",
    "regNo",
    "method",
    "employeeName",
    "status",
    "purposeName",
    "updatedFields",
    "removedItem",
    "previous",
    "doctorId",
    "patientId",
    "drugsId",
    "doseId",
    "purposeId",
    "productId",
    "categoryId",
    "brandId",
    "supplierId",
    "netTotal",
    "total",
    "scale",
    "unit",
    "newPrice",
    "alertQuantity",
    "addedPayments",
    "addedProductLines",
    "type",
    "suppliercompany",
  ];
  for (const key of Object.keys(d)) {
    if (knownKeys.includes(key)) continue;
    const v = d[key];
    if (v == null) continue;
    const str = typeof v === "object" ? JSON.stringify(v) : String(v).trim();
    if (str) parts.push(`${key}: ${str}`);
  }
  if (parts.length) return parts.join(", ");
  // Nothing but the patient was in details and the caller already shows it.
  return options.omitPatient ? "—" : JSON.stringify(details);
}

/** Rows that carry the patient in their own columns do not repeat it in the sentence. */
export function hasPatientColumns(entry: Pick<LogEntry, "patientName" | "regNo">): boolean {
  return Boolean((entry.patientName && entry.patientName.trim()) || (entry.regNo && entry.regNo.trim()));
}

export function getReadableDescription(entry: LogEntry): string {
  const actionText = getActionLabel(entry.action);
  const entityText = getEntityLabel(entry.entity);
  const detailStr = formatDetailsReadable(entry.details, { omitPatient: hasPatientColumns(entry) });
  if (detailStr && detailStr !== "—") {
    return `${actionText} (${entityText}) — ${detailStr}`;
  }
  return `${actionText} (${entityText})`;
}

export function formatDetails(details: string | object | undefined): string {
  const str = formatDetailsReadable(details);
  return str.length > 100 ? str.slice(0, 97) + "…" : str;
}

export function userName(entry: LogEntry): string {
  const u = entry.userId;
  if (!u) return "-";
  const first = u.firstName || "";
  const last = u.lastName || "";
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || u.employeeId || entry.userId?._id || "-";
}
